import { type CsticId } from "~/features/configurator/Linear/models";
import { type SubstepType } from "~/features/configurator/Linear/dtos";
import { selectCsticExpanded } from "~/features/configurator/Linear/redux/selectCstic";
import { updateCsticValues, updateSubstepAdditionalData } from "~/features/configurator/Linear/redux/actions";
import { useDispatch, useSelector } from "react-redux";
import { type ReactNode, useMemo } from "react";
import { isNonNull } from "~/common/utils/reducers";
import LanguageAwareExternalContent from "~/features/configurator/Linear/components/LanguageAwareExternalContent";

interface Props {
  csticId: CsticId;
  substep: SubstepType;
  csticsToNull?: string[];
  csticsToSet?: Array<CsticToSet | Resolver>;
  allowedValues?: string[];
  disabled?: boolean;
  required?: boolean;
  useCsticLabel?: boolean;
}

type CsticToSet =
  | {
      key: string;
      value: string | null;
    }
  | undefined;

type Resolver = (value: string | null) => CsticToSet;

/**
 * This hook provides all props necessary to connect a cstic to a SimpleSelect
 * @param csticId - The cstic to connect
 * @param substep - The substep the input is in, needed for updateCsticValues action
 * @param csticsToNull - Cstics that should be set to null when the input is changed
 */
export const useCsticSelectProps = ({
  csticId,
  substep,
  csticsToNull = [],
  csticsToSet = [],
  allowedValues,
  required,
  disabled: externalDisabled,
  useCsticLabel = false,
}: Props) => {
  const { cstic, isInConflict, isDirty } = useSelector(selectCsticExpanded(csticId, substep));
  const values = useMemo(() => {
    let values = cstic?.values ?? [];
    if (allowedValues) {
      values = values.filter((value) => allowedValues.includes(value.value));
    }
    return values;
  }, [cstic?.values, allowedValues]);

  const value = cstic?.value ?? null;

  const dispatch = useDispatch();
  const onChange = (value: string | null) => {
    dispatch(
      updateCsticValues(
        [
          {
            ...csticId,
            value,
          },
          ...csticsToNull.map((key) => ({
            ...csticId,
            key,
            value: null,
          })),
          ...resolveCsticsToSet(csticsToSet, value)
            .filter(isNonNull)
            .map(({ key, value }) => ({
              ...csticId,
              key,
              value,
            })),
        ],
        substep,
      ),
    );
    dispatch(
      updateSubstepAdditionalData(
        [
          {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            value: true as any,
            ...csticId,
          },
        ],
        substep,
      ),
    );
  };

  const selectProps: {
    onChange: (value: string | null) => void;
    value: string | null;
    error?: boolean;
    disabled?: boolean;
    label?: ReactNode;
  } = {
    onChange,
    value,
    error: isDirty && (isInConflict || (required && !value)),
    // error: isInConflict,
    disabled: externalDisabled,
  };

  if (useCsticLabel) {
    selectProps.label = <LanguageAwareExternalContent content={cstic?.label} className={"scha__lc__external-label"} />;
  }

  return {
    selectProps,
    cstic,
    csticValues: values,
  };
};

function resolveCsticsToSet(csticsToSet: Array<CsticToSet | Resolver>, value: string | null) {
  return csticsToSet.map((csticToSet) => {
    if (typeof csticToSet === "function") {
      return csticToSet(value);
    }
    return csticToSet;
  });
}
